import { observable, action, autorun } from 'mobx';

import ControlledFeatureService from 'services/ControlledFeatureService';
import CookieStorageService from 'services/CookieStorageService';
import messagingService from 'services/MessagingService/MessagingService';
import { GET_USER_DATA_RESPONSE } from 'services/MessagingService/EventNames';
import UserStore from 'stores/UserStore';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { GENDER } from 'enum/profile';
import allUserGroups from 'enum/userGroups';


export const GENDER_MAPPING = {
  STRAIGHT: 'straight',
  GAY: 'gay',
  TRANS: 'trans',
};

class AdStore {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(GET_USER_DATA_RESPONSE, this._handleUserChange);

    autorun(this.#syncUserStore);
  }

  destructor() {
    this.messagingService.off(GET_USER_DATA_RESPONSE, this._handleUserChange);
  }

  @observable user = UserStore?.user;
  @observable canShowAds = true;
  @observable genderType = GENDER_MAPPING.STRAIGHT;

  @action setGenderType = (gender) => {
    switch (gender) {
      case GENDER.MALE:
        this.genderType = GENDER_MAPPING.GAY;
        break;

      case GENDER.SHEMALE:
      case GENDER.TRANS:
      case GENDER.TRANSGENDER:
        this.genderType = GENDER_MAPPING.TRANS;
        break;

      default:
        this.genderType = GENDER_MAPPING.STRAIGHT;
    }
  };

  @action _handleUserChange = (user) => {
    this.user = user;

    this.canShowAds =
      this._getCanShowAnyAds() &&
      this._getCanShowAdsToCurrentUser();
  };

  _detectAdBlocker = () => {
    const divNode = document.createElement('div');
    divNode.setAttribute('id', 'detectAdsBlock');
    document.body.appendChild(divNode);

    const bannedClasses = ['ad', 'ads', 'adsbox', 'ad-placement', 'doubleclick', 'ad-placeholder', 'ad-badge'];
    divNode.classList.add(...bannedClasses);

    const getProperty = window.getComputedStyle(divNode).getPropertyValue('display');
    const isAdBlockEnabled = getProperty === 'none';

    document.body.removeChild(divNode);

    return isAdBlockEnabled;
  };

  _getCanShowAnyAds = () => {
    if (window.IS_BOT) {
      return false;
    }

    const isTier3Country = ControlledFeatureService.setUser(this.user).has(allUserGroups.REMOVE_ALL_ADS);
    if (isTier3Country) {
      return false;
    }

    if (this._detectAdBlocker()) {
      return false;
    }

    return true;
  };

  _getCanShowAdsToCurrentUser = () => {
    if (!this.user) {
      return true;
    }

    const { sfw, accessLevel, isPerformer } = this.user;
    if (sfw || isPerformer) {
      return false;
    }

    const isPremiumUser = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
    if (isPremiumUser) {
      return false;
    }

    const hideAdsCookie = CookieStorageService.get('cam4-HideAds') === 'true';
    const isGuest = accessLevel < ACCESS_LEVEL_NUM.BASIC;
    const isReferralGuest = isGuest && hideAdsCookie;
    if (isReferralGuest) {
      return false;
    }

    return true;
  };

  #syncUserStore = () => this._handleUserChange(UserStore.user);
}

export default new AdStore();
